import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ITireManufacturer, defaultValue } from 'app/shared/model/tire-manufacturer.model';

const initialState: EntityState<ITireManufacturer> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/tire-manufacturers';

// Actions

interface IPagedTireManufacturerQueryParams extends IQueryParams {
  nameSearch?: string;
}

export const getEntities = createAsyncThunk('tireManufacturer/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ITireManufacturer[]>(requestUrl);
});

export const getEntitiesByNameSearch = createAsyncThunk(
  'tireManufacturer/fetch_entity_list',
  async ({ page, size, sort, nameSearch }: IPagedTireManufacturerQueryParams) => {
    const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : '?'}cacheBuster=${new Date().getTime()}`;
    return axios.get<ITireManufacturer[]>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  'tireManufacturer/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ITireManufacturer>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'tireManufacturer/create_entity',
  async (entity: ITireManufacturer) => {
    const result = await axios.post<ITireManufacturer>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'tireManufacturer/update_entity',
  async (entity: ITireManufacturer) => {
    const result = await axios.put<ITireManufacturer>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'tireManufacturer/partial_update_entity',
  async (entity: ITireManufacturer) => {
    const result = await axios.patch<ITireManufacturer>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'tireManufacturer/delete_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ITireManufacturer>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const TireManufacturerSlice = createEntitySlice({
  name: 'tireManufacturer',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesByNameSearch), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntitiesByNameSearch), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = TireManufacturerSlice.actions;

// Reducer
export default TireManufacturerSlice.reducer;
